import { ReactElement, ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
// Design System
import { Popin } from '@utmb/design-system/popin/Popin';
// Styles
import styles from './help-button.module.scss';

// Either `children` (opened in a popin managed by the button) or a custom `onClick`, never both.
type HelpButtonProps = {
    ariaLabel: string;
    className?: string;
    /** White variant, for use on a dark background. */
    isDark?: boolean;
} & ({ children: ReactNode; onClick?: never } | { onClick: () => void; children?: never });

// Round "?" button, typically placed next to a label to open an explanation.
export const HelpButton = (props: HelpButtonProps): ReactElement => {
    const { ariaLabel, className, isDark = false, children, onClick } = props;

    const [isPopinOpen, setIsPopinOpen] = useState<boolean>(false);

    return (
        <>
            <button
                type="button"
                className={`${styles.button} ${isDark ? styles.dark : ''} ${className ?? ''}`}
                onClick={onClick ?? (() => setIsPopinOpen(true))}
                aria-label={ariaLabel}
            >
                ?
            </button>
            {/* Portaled to <body> so it isn't clipped or offset when the button sits inside another popin. */}
            {isPopinOpen &&
                createPortal(
                    <Popin closeCallback={() => setIsPopinOpen(false)} displayBorderRadius glassBorder>
                        <div className={styles.content}>{children}</div>
                    </Popin>,
                    document.body
                )}
        </>
    );
};
